import { Spinner } from '@vkontakte/vkui';
import { clsx } from 'clsx';
import { useId, useState, type ChangeEvent } from 'react';
import { errorMessage } from '../../api/client';
import { t } from '../../i18n';
import { HexIcon } from '../icons';
import { uploadImage } from '../lib/media';
import { Action } from './Action';
import { Muted } from './Muted';
import { Thumb } from './Thumb';
import styles from './ui.module.css';

/**
 * Поле «фото» в форме.
 *
 * Прежний вид — голый <img> на 160 px и две кнопки под ним — был набором
 * элементов, а не полем: непонятно, куда нажимать, чтобы выбрать, и нельзя
 * разглядеть уже выбранное. Здесь пустое поле само является областью выбора,
 * а заполненное открывается на весь экран по нажатию.
 *
 * Выбор файла держится на обычном input[type=file] внутри <label>: в VK Bridge
 * метода выбора файла нет вовсе, а label вместо div с onClick нужен, чтобы
 * поле ловилось с клавиатуры и читалось скринридером.
 *
 * Про подводные камни загрузки с телефона — раздел «Что проверить на реальном
 * телефоне» в корневом README: HEIC с айфона, поворот по EXIF и выгрузка
 * вебвью на Android при открытом пикере.
 */

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/avif,image/heic,image/heif';

export type ImageFieldVariant = 'field' | 'hero';

export interface ImageFieldProps {
  /**
   * «Фото питомца», «Паспорт», «Изображение внешности». Наружу не выводится:
   * подпись поля рисует FormItem через FieldLabel, как у всех остальных
   * полей. Здесь она нужна альтернативному тексту снимка.
   */
  label: string;
  imageId: string | null;
  onChange: (imageId: string | null) => void;
  disabled?: boolean;
  /**
   * Пояснение ВНУТРИ рамки, слева от команд. Снаружи, подписью под полем, оно
   * отрывалось от кадра и читалось как примечание к следующему полю.
   */
  hint?: string;
  variant?: ImageFieldVariant;
}

export function ImageField({
  label,
  imageId,
  onChange,
  disabled,
  hint,
  variant = 'field',
}: ImageFieldProps) {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const busy = disabled === true || uploading;

  const pick = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Сбрасываем значение сразу: иначе повторный выбор того же файла после
    // неудачной загрузки не даст события change.
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    setError(null);
    try {
      const uploaded = await uploadImage(file);
      onChange(uploaded.id);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setUploading(false);
    }
  };

  const input = (
    <input
      id={inputId}
      className={styles.imageFieldInput}
      type="file"
      accept={ACCEPT}
      disabled={busy}
      onChange={(event) => void pick(event)}
    />
  );

  if (imageId !== null) {
    return (
      <>
        {/* Заполненное поле — портретный кадр слева, команды столбиком
            справа: так оно набрано в макете правки внешки. Сюда грузят лица,
            и квадратный кадр срезал их по подбородок. */}
        <div className={variant === 'hero' ? styles.imageFieldBox : styles.imageFieldBoxed}>
          <div
            className={variant === 'hero' ? styles.imageFieldThumb : styles.imageFieldThumbSmall}
          >
            <Thumb
              image={{ id: imageId }}
              fill
              ratio={variant === 'hero' ? 'portrait' : 'square'}
              alt={label}
              gallery={[{ id: imageId }]}
            />
          </div>

          <div className={styles.imageFieldSide}>
            {/* В поле формы пояснение стоит НАД командами, а команды прижаты к
                нижнему краю кадра: так они встают вровень с его основанием. */}
            {variant === 'field' && hint !== undefined ? (
              <div className={styles.imageFieldNote}>{hint}</div>
            ) : null}

            <div
              className={clsx(
                styles.imageFieldActions,
                variant !== 'hero' && styles.imageFieldActionsRow,
              )}
            >
              {/* Кнопка и label — соседи, а не вложенные: кнопка внутри кнопки запрещена. */}
              {/* Без htmlFor: input лежит ВНУТРИ label, и вместе с htmlFor
                нажатие в части браузеров срабатывало бы дважды. */}
              {/* Без значка: в макете команды фото набраны одной подписью. */}
              <Action tone="accent" size="s" disabled={busy} Component="label" loading={uploading}>
                {t('ui.image.replace')}
                {input}
              </Action>
              <Action tone="quiet" size="s" disabled={busy} onClick={() => onChange(null)}>
                {t('ui.image.remove')}
              </Action>
            </div>

            {variant === 'hero' && hint !== undefined ? (
              <div className={styles.imageFieldHint}>{hint}</div>
            ) : null}
          </div>
        </div>

        {error !== null ? <Muted tone="negative">{error}</Muted> : null}
      </>
    );
  }

  const invite = uploading ? (
    <Spinner size="m" />
  ) : (
    <>
      {/* Значок по размеру плитки: в поле формы она 82×88, и тридцать
          пикселей значка съедали её вместе с подписью. */}
      <HexIcon size={variant === 'field' ? 24 : 33} plus tone="violet" />
      {t('ui.image.pick')}
    </>
  );

  /*
    Пустое поле формы носит ТУ ЖЕ коробку, что и заполненное: рамка, кадр
    слева, пояснение справа. Пока оно было голой плиткой 82×88, поле меняло
    облик от заполнения, а нажимать приходилось в плитку — остальная ширина
    строки не работала вовсе.
  */
  if (variant === 'field') {
    return (
      <>
        <label
          className={clsx(
            styles.imageFieldBoxed,
            styles.imageFieldPickBox,
            uploading && styles.imageFieldPickBusy,
          )}
        >
          {input}
          <span className={clsx(styles.imageFieldPick, styles.imageFieldPickSmall)}>{invite}</span>
          {hint === undefined ? null : (
            <span className={styles.imageFieldSide}>
              <span className={styles.imageFieldNote}>{hint}</span>
            </span>
          )}
        </label>

        {error !== null ? <Muted tone="negative">{error}</Muted> : null}
      </>
    );
  }

  return (
    <div className={styles.imageField}>
      <label className={clsx(styles.imageFieldPick, uploading && styles.imageFieldPickBusy)}>
        {input}
        {invite}
      </label>

      {error !== null ? <Muted tone="negative">{error}</Muted> : null}
    </div>
  );
}
